import React, { useEffect, useState } from 'react'
import { MessageCircle, Send, Plus, Pencil, Trash2 } from 'lucide-react'
import api from '../api'
import { Card, PageHeader, Field, Input, Textarea, Button, Alert, Badge, Table, THead, td, Modal, ModalHeader } from '../components/ui'

// Fase 1 "Template WA Tagihan" (2026-07-26), diganti jadi sistem modular hari yang sama --
// dulu satu textarea/app_config, sekarang manajer list (tabel wa_template, lihat
// server/routes/waTemplate.js) supaya admin bisa Tambah/Edit/Hapus template sendiri tanpa perlu
// deploy kode tiap ada kebutuhan template baru. metode_pembayaran & toggle kirim otomatis TETAP
// app_config (bukan template, cuma data/setting) via GET/POST /tagihan-wa/template spt sebelumnya.
const PLACEHOLDER_REF = [
  ['{nama}', 'Nama pelanggan'],
  ['{paket}', 'Nama paket langganan'],
  ['{periode}', 'Bulan & tahun tagihan (mis. Juli 2026)'],
  ['{status}', 'Belum Lunas / Sebagian / Lunas'],
  ['{jumlah}', 'Sisa yang harus dibayar (Rp)'],
  ['{diskon}', 'Baris diskon bonus, mis. "Diskon 10%: -Rp 15.000" (taruh di baris sendiri -- otomatis hilang kalau tagihan tanpa diskon)'],
  ['{jatuh_tempo}', 'Tanggal jatuh tempo'],
  ['{metode_pembayaran}', 'Isi dari card Metode Pembayaran di bawah'],
  ['{link_invoice}', 'Link halaman tagihan publik pelanggan']
]

function SectionTitle({ icon: Icon, children }) {
  return (
    <h2 style={{ fontSize: 14.5, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 8, color: 'var(--text)' }}>
      <Icon size={16} color="var(--brand)" />{children}
    </h2>
  )
}

export default function PengaturanTagihanWa() {
  const [templates, setTemplates] = useState([])
  const [metodePembayaran, setMetodePembayaran] = useState('')
  const [autoKirimEnabled, setAutoKirimEnabled] = useState(false)
  const [isolirAutoKirimEnabled, setIsolirAutoKirimEnabled] = useState(false)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [togglingAutoKirim, setTogglingAutoKirim] = useState(false)
  const [togglingIsolirAutoKirim, setTogglingIsolirAutoKirim] = useState(false)
  const [pesan, setPesan] = useState(null)

  const [modal, setModal] = useState(null) // null | { id?, kode, nama, isi }
  const [modalError, setModalError] = useState('')
  const [modalSaving, setModalSaving] = useState(false)

  useEffect(() => { load() }, [])

  async function load() {
    setLoading(true)
    try {
      const [tplRes, res] = await Promise.all([
        api.get('/wa-template'),
        api.get('/tagihan-wa/template')
      ])
      setTemplates(tplRes.data)
      setMetodePembayaran(res.data.metode_pembayaran)
      setAutoKirimEnabled(res.data.auto_kirim_enabled)
      setIsolirAutoKirimEnabled(res.data.isolir_auto_kirim_enabled)
    } finally { setLoading(false) }
  }

  async function simpanMetode() {
    setPesan(null)
    setSaving(true)
    try {
      await api.post('/tagihan-wa/template', { metode_pembayaran: metodePembayaran })
      setPesan({ type: 'success', text: 'Metode pembayaran tersimpan.' })
    } catch (e) {
      setPesan({ type: 'error', text: e.response?.data?.message || 'Gagal menyimpan' })
    } finally { setSaving(false) }
  }

  async function toggleAutoKirim() {
    setPesan(null)
    setTogglingAutoKirim(true)
    try {
      await api.post('/tagihan-wa/template', { auto_kirim_enabled: !autoKirimEnabled })
      setAutoKirimEnabled(!autoKirimEnabled)
    } catch (e) {
      setPesan({ type: 'error', text: e.response?.data?.message || 'Gagal mengubah toggle' })
    } finally { setTogglingAutoKirim(false) }
  }

  async function toggleIsolirAutoKirim() {
    setPesan(null)
    setTogglingIsolirAutoKirim(true)
    try {
      await api.post('/tagihan-wa/template', { isolir_auto_kirim_enabled: !isolirAutoKirimEnabled })
      setIsolirAutoKirimEnabled(!isolirAutoKirimEnabled)
    } catch (e) {
      setPesan({ type: 'error', text: e.response?.data?.message || 'Gagal mengubah toggle' })
    } finally { setTogglingIsolirAutoKirim(false) }
  }

  function bukaTambah() {
    setModal({ kode: '', nama: '', isi: '' })
    setModalError('')
  }

  function bukaEdit(t) {
    setModal({ id: t.id, kode: t.kode, nama: t.nama, isi: t.isi })
    setModalError('')
  }

  async function simpanModal() {
    setModalError('')
    setModalSaving(true)
    try {
      if (modal.id) {
        await api.post(`/wa-template/${modal.id}`, { nama: modal.nama, isi: modal.isi })
      } else {
        await api.post('/wa-template', { kode: modal.kode, nama: modal.nama, isi: modal.isi })
      }
      setModal(null)
      load()
    } catch (e) {
      setModalError(e.response?.data?.message || 'Gagal menyimpan template')
    } finally { setModalSaving(false) }
  }

  async function hapusTemplate(t) {
    if (!window.confirm(`Hapus template "${t.nama}"?`)) return
    await api.delete(`/wa-template/${t.id}`)
    load()
  }

  if (loading) return null

  return (
    <div>
      <PageHeader title="Template WA Tagihan" subtitle="Kelola template pesan WhatsApp billing -- bisa tambah lebih dari satu" />

      {pesan && <Alert type={pesan.type}>{pesan.text}</Alert>}

      <Card style={{ padding: 24, marginBottom: 20 }}>
        <SectionTitle icon={Send}>Kirim Otomatis</SectionTitle>
        <p style={{ fontSize: 12.5, color: 'var(--text-muted)', marginBottom: 16, marginTop: 6 }}>
          Kalau nyala, WA tagihan otomatis terkirim ke pelanggan tiap kali generate tagihan bulanan berjalan (tanggal 1, jam 05:00 WIB). Generate tagihan-nya sendiri TETAP jalan otomatis walau ini mati -- kirim WA tetap bisa manual per baris di halaman Tagihan.
        </p>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <Badge tone={autoKirimEnabled ? 'success' : 'neutral'}>{autoKirimEnabled ? 'Aktif' : 'Nonaktif'}</Badge>
          <Button variant={autoKirimEnabled ? 'danger' : 'success'} loading={togglingAutoKirim} onClick={toggleAutoKirim}>
            {autoKirimEnabled ? 'Matikan Kirim Otomatis' : 'Nyalakan Kirim Otomatis'}
          </Button>
        </div>
      </Card>

      <Card style={{ padding: 24, marginBottom: 20 }}>
        <SectionTitle icon={Send}>Kirim Otomatis saat Isolir</SectionTitle>
        <p style={{ fontSize: 12.5, color: 'var(--text-muted)', marginBottom: 16, marginTop: 6 }}>
          Kalau nyala, WA notif isolir (template kode <code>isolir_notice</code>) otomatis terkirim ke pelanggan begitu statusnya baru berubah jadi ISOLIR (dari cron Isolir Otomatis tiap 30 menit / trigger instan). Terpisah dari efek jaringan (limit bandwidth/captive portal) -- ini murni notifikasi WA. Bisa juga dikirim manual per baris di halaman Tagihan lewat tombol "Kirim Notif Isolir" walau toggle ini mati.
        </p>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <Badge tone={isolirAutoKirimEnabled ? 'success' : 'neutral'}>{isolirAutoKirimEnabled ? 'Aktif' : 'Nonaktif'}</Badge>
          <Button variant={isolirAutoKirimEnabled ? 'danger' : 'success'} loading={togglingIsolirAutoKirim} onClick={toggleIsolirAutoKirim}>
            {isolirAutoKirimEnabled ? 'Matikan Kirim Otomatis' : 'Nyalakan Kirim Otomatis'}
          </Button>
        </div>
      </Card>

      <Card style={{ padding: 24, marginBottom: 20 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
          <SectionTitle icon={MessageCircle}>Template Pesan</SectionTitle>
          <Button size="sm" icon={Plus} onClick={bukaTambah}>Tambah Template</Button>
        </div>
        <p style={{ fontSize: 12.5, color: 'var(--text-muted)', marginBottom: 16 }}>
          Template dgn kode <code>tagihan_baru</code> dipakai tombol "Kirim WA" di halaman Tagihan & saat kirim otomatis. Template lain bebas ditambah utk kebutuhan ke depan.
        </p>
        <Table>
          <THead cols={['Nama', 'Kode', 'Status', '']} />
          <tbody>
            {templates.map(t => (
              <tr key={t.id}>
                <td style={{ ...td, fontWeight: 600 }}>{t.nama}</td>
                <td style={td}><code>{t.kode}</code></td>
                <td style={td}><Badge tone={t.aktif ? 'success' : 'neutral'}>{t.aktif ? 'Aktif' : 'Nonaktif'}</Badge></td>
                <td style={td}>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <Button size="sm" variant="soft" icon={Pencil} onClick={() => bukaEdit(t)}>Edit</Button>
                    <Button size="sm" variant="danger-soft" icon={Trash2} onClick={() => hapusTemplate(t)}>Hapus</Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </Card>

      <Card style={{ padding: 24, marginBottom: 20 }}>
        <SectionTitle icon={MessageCircle}>Metode Pembayaran</SectionTitle>
        <p style={{ fontSize: 12.5, color: 'var(--text-muted)', marginBottom: 16, marginTop: 6 }}>
          Isi info rekening/QRIS/instruksi pembayaran yang sebenarnya di sini sebelum dipakai kirim ke pelanggan asli — ini otomatis mengisi placeholder <code>{'{metode_pembayaran}'}</code> di template.
        </p>
        <Field label="Info Metode Pembayaran" style={{ marginBottom: 14 }}>
          <Textarea rows={4} value={metodePembayaran} onChange={e => setMetodePembayaran(e.target.value)}
            placeholder={'Contoh:\nTransfer BCA 1234567890 a.n. FazNet\nQRIS: scan di kantor / minta ke CS'} />
        </Field>
        <Button loading={saving} onClick={simpanMetode}>Simpan Metode Pembayaran</Button>
      </Card>

      {modal && (
        <Modal onClose={() => setModal(null)} width={760}>
          <ModalHeader title={modal.id ? 'Edit Template' : 'Tambah Template'} onClose={() => setModal(null)} />
          {modalError && <Alert type="error">{modalError}</Alert>}
          <div className="grid-sidebar-260" style={{ gap: 16 }}>
            <div>
              <Field label="Kode (identifier, tidak bisa diubah setelah dibuat) *" style={{ marginBottom: 14 }}>
                <Input value={modal.kode} disabled={!!modal.id}
                  onChange={e => setModal({ ...modal, kode: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '_') })}
                  placeholder="mis. tagihan_baru" />
              </Field>
              <Field label="Nama *" style={{ marginBottom: 14 }}>
                <Input value={modal.nama} onChange={e => setModal({ ...modal, nama: e.target.value })} placeholder="mis. Tagihan Baru" />
              </Field>
              <Field label="Isi Pesan *" style={{ marginBottom: 6 }}>
                <Textarea rows={12} value={modal.isi} onChange={e => setModal({ ...modal, isi: e.target.value })} />
              </Field>
            </div>
            <div>
              <div style={{ fontSize: 12.5, fontWeight: 700, marginBottom: 8, color: 'var(--text)' }}>Referensi Placeholder</div>
              {PLACEHOLDER_REF.map(([ph, desc]) => (
                <div key={ph} style={{ marginBottom: 10 }}>
                  <code style={{ fontSize: 12, color: 'var(--brand)' }}>{ph}</code>
                  <div style={{ fontSize: 11.5, color: 'var(--text-muted)' }}>{desc}</div>
                </div>
              ))}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
            <Button variant="neutral" onClick={() => setModal(null)} style={{ flex: 1, justifyContent: 'center' }}>Batal</Button>
            <Button loading={modalSaving} onClick={simpanModal} style={{ flex: 1, justifyContent: 'center' }}>Simpan</Button>
          </div>
        </Modal>
      )}
    </div>
  )
}
